import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AssumptionsPage.module.css';
import config, { logger } from '../../config';
import { generateOnboardingCharts, getOnboardingAIData, getToken, handleApiError } from '../../utils/api';
import {
  calculateAllFinancialDataAI,
  fetchSimulationOutputForCharts,
  normalizeOnboardingProgressKeys,
  onboardingProgressFromV2OnboardingAiResult,
} from '../../utils/onboardingUtils';
import { FINANCIAL_CONSTANTS, HEALTHCARE_COSTS, getMissingRequiredDashboardFields } from '../../utils/helper';
import { getGlobalTypography } from '../../utils/globalTypography';
import { useContentCatalog } from '../../utils/contentCatalog';
import ContentParts from '../shared/ContentParts';

const COLLEGE_YEARS = 4;
const HEALTHCARE_INFLATION_RATE = 4.5;
const FORMULA_NET_WORTH = 'Net Worth = Total Assets - Total Liabilities';

const TABLE_ERRORS = {
  incomplete: 'Unable to generate table: onboarding data is incomplete.',
  unavailable: 'Unable to generate table: simulation output is unavailable.',
  failed: 'Failed to generate table. Please try again.',
  empty: 'No tables were returned for this data.',
};

const formatCurrency = (n) => Number(n).toLocaleString('en-US');
const formatPct = (rate) => (rate * 100).toFixed(1);
const cx = (...parts) => parts.filter(Boolean).join(' ');

const RATES = {
  generalInflation: formatPct(FINANCIAL_CONSTANTS.INFLATION_RATE),
  healthcareInflation: HEALTHCARE_INFLATION_RATE,
  housingAppreciation: formatPct(FINANCIAL_CONSTANTS.FUTURE_VALUE_REAL_ESTATE_RATE),
  realGrowth: formatPct(FINANCIAL_CONSTANTS.CURRENT_VALUE_REAL_ESTATE_RATE),
  assumedReturn: formatPct(FINANCIAL_CONSTANTS.INVESTMENT_RETURN_RATE),
  realReturn: formatPct(FINANCIAL_CONSTANTS.CURRENT_VALUE_OTHER_ASSETS_RATE),
  rmdAge: FINANCIAL_CONSTANTS.RMD_START_AGE,
  planningHorizon: FINANCIAL_CONSTANTS.LIFE_EXPECTANCY,
  collegeCostLabel: `$${formatCurrency(FINANCIAL_CONSTANTS.COLLEGE_COST / COLLEGE_YEARS)} / year / child`,
  healthcareBufferLabel: `A buffer of additional $${formatCurrency(HEALTHCARE_COSTS.MARRIED)} for health care expenses is recommended.`,
};

function buildTableHtml(fragments, userName) {
  const safeName = userName || 'User';
  const summary = fragments?.summary_table || '';
  const detailed = fragments?.detailed_table || '';
  const fragmentStyles = fragments?.styles || '';
  const type = getGlobalTypography();
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Table - ${safeName}</title>${type.fontLinksHtml}${fragmentStyles}<style>
      *{box-sizing:border-box}
      body{margin:0;padding:1.5rem 1.25rem 2rem;font-family:${type.sans};font-size:17px;line-height:1.5;color:${type.muted};background:linear-gradient(135deg,#f8f9fa 0%,#e9ecef 100%);animation:rcgFadeIn 0.78s cubic-bezier(0.22,1,0.36,1) both}
      #summary-container,#detailed-container{margin-bottom:1.5rem}
      #detailed-container{margin-bottom:0}
      </style>
      </head><body>
      <div id="summary-container">${summary}</div>
      <div id="detailed-container">${detailed}</div>
      <script>(function(){try{window.scrollTo(0,0);var scrollers=document.querySelectorAll('.rcg-table-scroll');for(var i=0;i<scrollers.length;i++){scrollers[i].scrollLeft=0;}}catch(e){}})();</script>
      </body></html>`;
}

function unwrapOnboardingPayload(onboardingApiData) {
  const payload = onboardingApiData?.data ?? onboardingApiData;
  return payload?.data ?? payload;
}

async function resolveOnboardingProgress(root) {
  const cached = root?.v2_onboarding_ai_result;
  let progressRaw = null;
  if (cached && typeof cached === 'object' && !Array.isArray(cached)) {
    progressRaw = onboardingProgressFromV2OnboardingAiResult(cached) || cached.onboardingProgress || null;
  }

  const cachedProgress = normalizeOnboardingProgressKeys(progressRaw || {});
  if (getMissingRequiredDashboardFields(cachedProgress).length === 0) {
    return cachedProgress;
  }

  const chatData = root?.v2_onboarding_ai?.chat || [];
  if (chatData.length === 0) return null;

  const financialDataResult = await calculateAllFinancialDataAI(chatData, { useAdminEndpoint: false });
  return normalizeOnboardingProgressKeys(financialDataResult?.onboardingProgress || {});
}

function SubsectionHeader({ icon, title }) {
  return (
    <div className={styles.subsectionHeader}>
      <span className={styles.subsectionIcon} aria-hidden>{icon}</span>
      <h3 className={styles.subsectionTitle}>{title}</h3>
    </div>
  );
}

function AssumptionCard({ label, value, description, valueClassName, cardClassName, valueFirst }) {
  const valueEl = value != null ? (
    <div className={cx(styles.cardValue, valueClassName)}>{value}</div>
  ) : null;
  const labelEl = label ? <div className={styles.cardLabel}>{label}</div> : null;
  return (
    <div className={cx(styles.card, cardClassName)}>
      {valueFirst ? <>{valueEl}{labelEl}</> : <>{labelEl}{valueEl}</>}
      {description && <div className={styles.cardDescription}>{description}</div>}
    </div>
  );
}

function AssumptionList({ items, className }) {
  return (
    <ul className={cx(styles.assumptionList, className)}>
      {(items || []).map((item, index) => (
        <li key={index} className={styles.assumptionListItem}>
          {Array.isArray(item) ? <ContentParts parts={item} linkClassName={styles.link} /> : item}
        </li>
      ))}
    </ul>
  );
}

function CashFlowBlock({ title, children }) {
  return (
    <div className={styles.cashFlowSection}>
      {title && <h4 className={styles.cashFlowTitle}>{title}</h4>}
      {children}
    </div>
  );
}

function RmdItem({ label, value, description, note }) {
  return (
    <div className={styles.rmdItem}>
      <div className={styles.rmdLabel}>{label}</div>
      <div className={styles.rmdValue}>{value}</div>
      {description && <div className={styles.rmdDescription}>{description}</div>}
      {note && <div className={styles.rmdNoteInline}>{note}</div>}
    </div>
  );
}

function DetailedTableButton({ isLoading, onClick }) {
  return (
    <button
      type="button"
      className={styles.detailedTableButton}
      onClick={onClick}
      disabled={isLoading}
    >
      <span className={styles.detailedTableButtonIcon} aria-hidden>
        {isLoading ? <span className={styles.detailedTableButtonSpinner} /> : <TableIcon />}
      </span>
      {isLoading ? 'Preparing Table…' : 'Open Detailed Table'}
    </button>
  );
}

function TableIcon() {
  return (
    <svg className={styles.detailedTableButtonSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function DetailedTableModal({ isOpen, isLoading, error, html, onClose }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Detailed Table"
      onClick={onClose}
    >
      <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>Detailed Table</div>
          <button type="button" className={styles.modalCloseButton} onClick={onClose} aria-label="Close">
            <svg
              className={styles.modalCloseIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className={styles.modalBody}>
          {isLoading || !html ? (
            <div className={styles.modalLoading}>
              {error ? (
                <div className={styles.modalError}>{error}</div>
              ) : (
                <div className={styles.modalLoadingState}>
                  <span className={styles.modalSpinner} aria-hidden>
                    <span className={styles.modalSpinnerInner} />
                  </span>
                  <span>Generating your table…</span>
                </div>
              )}
            </div>
          ) : (
            <iframe
              className={styles.tableIframe}
              srcDoc={html}
              title="Detailed Table"
              sandbox="allow-scripts"
            />
          )}
        </div>
      </div>
    </div>
  );
}

const AssumptionsContent = memo(function AssumptionsContent({ isLoadingTable, onOpenDetailedTable }) {
  const { catalog } = useContentCatalog();
  const lists = catalog?.assumptions?.lists || {};
  const sectionTitle = catalog?.assumptions?.section_title || 'Assumptions';
  const sectionSubtitle = catalog?.assumptions?.section_subtitle || '';
  const planningAgeValue = `Current Age until Age ${RATES.planningHorizon}`;

  return (
    <div className={styles.mainContent}>
      <div className={styles.contentWrapper}>
        <section className={styles.section} aria-label="Assumptions">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeading}>
              <h2 className={styles.sectionTitle}>{sectionTitle}</h2>
              <p className={styles.sectionSubtitle}>{sectionSubtitle}</p>
            </div>
            <DetailedTableButton isLoading={isLoadingTable} onClick={onOpenDetailedTable} />
          </div>

          <div className={styles.subsection}>
            <SubsectionHeader icon="🏠" title="Inflation & Real Estate" />

            <div className={styles.cardsGrid}>
              <AssumptionCard
                label="GENERAL INFLATION"
                value={`${RATES.generalInflation}%`}
                description="Average annual increase in the cost of goods."
              />
              <AssumptionCard
                label="HEALTHCARE INFLATION"
                value={`${RATES.healthcareInflation}%`}
                description="Medical costs historically rise faster than general goods."
                valueClassName={styles.redValue}
              />
            </div>

            <div className={styles.housingAppreciation}>
              <div className={styles.housingRow}>
                <div className={styles.housingLabel}>
                  <span className={styles.housingPercent}>{RATES.housingAppreciation}%</span> – Annual growth rate of your real estate assets.
                </div>
              </div>
              <div className={styles.housingRow}>
                <div className={styles.housingLabel}>
                  <span className={styles.housingPercent}>{RATES.realGrowth}%</span> – Annual growth rate of your real estate assets, minus inflation.
                </div>
              </div>
              <div className={styles.infoBox}>
                <span className={styles.infoIcon} aria-hidden>ℹ️</span>
                <span className={styles.infoText}>Growth is calculated on the original home value only, not compounded annually.</span>
              </div>
            </div>
          </div>

          <div className={styles.subsection}>
            <SubsectionHeader icon="📊" title="Net Worth & Investments" />

            <div className={styles.formulaBar}>{FORMULA_NET_WORTH}</div>

            <div className={styles.cardsGrid}>
              <AssumptionCard
                label="CURRENT DOLLAR NET WORTH"
                description="Snapshot of assets minus liabilities valued in today's dollars, unadjusted for future inflation or growth."
              />
              <AssumptionCard
                label="FUTURE DOLLAR NET WORTH"
                description="Projected value of assets minus liabilities at a future date, stated in nominal future dollars including expected investment growth and inflation."
              />
            </div>

            <div className={styles.investmentReturns}>
              <div className={styles.cardsGrid}>
                <AssumptionCard
                  value={`${RATES.assumedReturn}% (Compound)`}
                  label="ASSUMED RATE OF RETURN"
                  valueFirst
                />
                <AssumptionCard
                  value={`${RATES.realReturn}% (Compound)`}
                  label="ASSUMED RATE OF RETURN MINUS INFLATION"
                  valueFirst
                />
              </div>
              <div className={styles.descriptionText}>
                Applied to all investment assets, including Pre-Tax, Roth, Equity Comp, Post-Tax, and Brokerage accounts, but not applied to real estate assets. Contributions are added before growth is applied.
              </div>
              <div className={styles.comingSoon}>
                When we launch the upcoming Scenario Modeling feature, you will be able to set Assumed Rates of Return for various asset types.
              </div>
            </div>
          </div>

          <div className={styles.subsection}>
            <SubsectionHeader icon="💬" title="Career Shift & Longevity" />

            <CashFlowBlock title="Career Shift Runway">
              <p className={styles.cashFlowDescription}>
                <strong>Accessible Short-Term Liquidity:</strong>
              </p>
              <p className={styles.cashFlowDescription}>
                Defined as quickly available with zero to low penalty, including savings, brokerage accounts,  and an allowable loan from pre-tax retirement accounts.
              </p>
              <p className={styles.cashFlowDescription}>
                Allowable loan is max $50,000 OR 50% of investment balance, if the balance is less than $100,000.
              </p>
              <p className={styles.cashFlowDescription}>
                This is an isolated short-term liquidity view, which merits an assessment of impact to long-term retirement projections available in upcoming product release.
              </p>
            </CashFlowBlock>

            <CashFlowBlock title="Longevity Score">
              <p className={styles.cashFlowDescription}>
                <strong>Your Longevity Score</strong> shows how confident you can be that your money will last throughout your lifetime.
              </p>
              <AssumptionList items={lists.longevity} />
            </CashFlowBlock>

            <div className={styles.rmdSection}>
              <h4 className={styles.rmdTitle}>RMD Age & Planning Horizon</h4>
              <div className={styles.rmdGrid}>
                <RmdItem
                  label="RMD Age"
                  value={RATES.rmdAge}
                  description="(Auto-updates based on birth year)"
                  note={`Accounts grow at ${RATES.assumedReturn}% compound interest. At age ${RATES.rmdAge}, we divide the balance by the distribution period (e.g., 24.6 years) to calculate the forced withdrawal.`}
                />
                <RmdItem
                  label="Planning Age (User)"
                  value={planningAgeValue}
                  description="(Default)"
                />
                <RmdItem
                  label="Planning Age (Partner)"
                  value={planningAgeValue}
                  description="(Default)"
                />
              </div>
              <div className={styles.engineDescription}>
                Required Minimum Distribution (RMD) is the minimum amount you're required to withdraw annually from tax-deferred retirement accounts like traditional IRAs or 401(k)s starting at age 73 (or 75 if born in 1960 or later). This IRS rule ensures the government collects taxes on those savings, with penalties up to 25% if you miss it.
              </div>
            </div>
          </div>

          <div className={styles.subsection}>
            <SubsectionHeader icon="📈" title="Cash Flow Modeling" />

            <CashFlowBlock title="COLA Adjustments">
              <p className={styles.cashFlowDescription}>
                All income and expenses reflect a cost of living adjustment at the general rate of inflation. This includes income from Work, Social Security, Pension & Annuity.
              </p>
            </CashFlowBlock>

            <CashFlowBlock title="Base Expenses">
              <p className={styles.cashFlowDescription}>
                We currently assume that your monthly baseline expenses are annualized and remain constant in pre-retirement. During retirement, we assume that your monthly baseline expenses will drop to 78% of pre-retirement expenses through age 95 in the charts. You will be able to set expenses at different levels for specified periods of time in our upcoming Scenario Modeling feature. For example you will be able to model a drop in monthly mortgage expenses or an increase in healthcare expenses later in life.
              </p>

              <div className={styles.cardsGrid}>
                <AssumptionCard
                  label="College Costs"
                  value={RATES.collegeCostLabel}
                  description={`Current projections model this fixed cost for ${COLLEGE_YEARS} years per child. If years overlap for multiple children, costs stack.`}
                />
                <AssumptionCard
                  label="Health Care Expenses"
                  value={RATES.healthcareBufferLabel}
                  description="Current projections model this expense. You will be able to add granular health care expenses in our upcoming Scenario Modeling feature."
                  cardClassName={styles.healthcareCard}
                />
              </div>

              <h4 className={styles.cashFlowTitle}>Work Income</h4>
              <p className={styles.cashFlowDescription}>
                We currently only include your work income during your career transition and after your career shift once your new direction stabilizes. You will be able to set work income at different levels for specified periods of time in our upcoming Scenario Modeling feature. For example, you will be able to model increased work income as a result of a new career or business direction.
              </p>

              <h4 className={styles.cashFlowTitle}>Social Security Income</h4>
              <AssumptionList items={lists.social_security} />
            </CashFlowBlock>

            <CashFlowBlock title="Debt Payments">
              <p className={styles.cashFlowDescription}>
                This model assumes amortization schedules based on average midlife scenarios with the following payment timelines and percentage interest for each debt category:
              </p>
              <AssumptionList items={lists.debt} />
            </CashFlowBlock>

            <div className={styles.drawdownSection}>
              <h4 className={styles.drawdownTitle}>Savings Drawdown Logic</h4>
              <AssumptionList items={lists.drawdown} className={styles.drawdownList} />
            </div>

            <CashFlowBlock title="Taxes">
              <p className={styles.cashFlowDescription}>This model assumes rough tax estimates only:</p>
              <AssumptionList items={lists.tax} />
            </CashFlowBlock>
          </div>
        </section>
      </div>
    </div>
  );
});

const AssumptionsPage = () => {
  const navigate = useNavigate();
  const tableAbortRef = useRef(null);

  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [tableError, setTableError] = useState('');
  const [tableHtml, setTableHtml] = useState(null);

  const closeTableModal = useCallback(() => {
    tableAbortRef.current?.abort();
    tableAbortRef.current = null;
    setIsTableModalOpen(false);
    setIsLoadingTable(false);
    setTableError('');
    setTableHtml(null);
  }, []);

  const handleOpenDetailedTable = useCallback(async () => {
    if (tableAbortRef.current) return;

    const controller = new AbortController();
    tableAbortRef.current = controller;
    const { signal } = controller;

    setIsTableModalOpen(true);
    setIsLoadingTable(true);
    setTableError('');
    setTableHtml(null);

    try {
      const token = getToken();
      if (!token) {
        handleApiError(new Error('No token found'), navigate, false);
        return;
      }

      const onboardingApiData = await getOnboardingAIData(token, navigate, false, signal);
      if (signal.aborted) return;
      if (!onboardingApiData) {
        setTableError(TABLE_ERRORS.failed);
        return;
      }

      const onboardingProgress = await resolveOnboardingProgress(unwrapOnboardingPayload(onboardingApiData));
      if (signal.aborted) return;
      if (!onboardingProgress) {
        setTableError(TABLE_ERRORS.incomplete);
        return;
      }

      const { output, requestBody } = await fetchSimulationOutputForCharts(onboardingProgress, signal);
      if (signal.aborted) return;
      if (!output) {
        setTableError(TABLE_ERRORS.unavailable);
        return;
      }

      const name = localStorage.getItem(config.STORAGE_KEYS.NAME) || 'User';
      const { ok, fragments, message } = await generateOnboardingCharts(
        {
          output,
          input: requestBody,
          case_id: 'Report',
          user_name: name,
          include_configuration: false,
        },
        { signal, navigate, isAdmin: false }
      );

      if (signal.aborted || message === 'Request cancelled.') return;
      if (message === 'Session expired. Please sign in again.') return;
      if (!ok) {
        setTableError(TABLE_ERRORS.failed);
        return;
      }

      if (!fragments?.summary_table && !fragments?.detailed_table) {
        logger.warn('Generator returned no table fragments:', Object.keys(fragments || {}));
        setTableError(TABLE_ERRORS.empty);
        return;
      }

      setTableHtml(buildTableHtml(fragments, name));
    } catch (error) {
      if (error?.name === 'AbortError' || signal.aborted) return;
      logger.error('Error generating table:', error);
      setTableError(TABLE_ERRORS.failed);
    } finally {
      if (tableAbortRef.current === controller) {
        tableAbortRef.current = null;
        setIsLoadingTable(false);
      }
    }
  }, [navigate]);

  useEffect(() => () => {
    tableAbortRef.current?.abort();
  }, []);

  return (
    <div className={styles.assumptionsPage}>
      <DetailedTableModal
        isOpen={isTableModalOpen}
        isLoading={isLoadingTable}
        error={tableError}
        html={tableHtml}
        onClose={closeTableModal}
      />

      <AssumptionsContent
        isLoadingTable={isLoadingTable}
        onOpenDetailedTable={handleOpenDetailedTable}
      />
    </div>
  );
};

export default AssumptionsPage;
